import { IApplicationFlag } from "@rentiohq/shared-frontend/dist/types/application.types";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Tooltip } from "@rentiohq/web-shared/dist/components";
import styled, { css } from "@rentiohq/web-theme";
import React from "react";
import { RM_COLORS } from "../rentmatch.colors";
import { BARE_TOOLTIP_TRIGGER_STYLE } from "../tooltipTrigger.styles";

export type TFlagLabelVariant = "inline" | "large";

const large = css`
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
`;

const inline = css`
  height: 18px;
  padding: 0 5px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
`;

// Filled with the darkest neutral, unlike the pale no-show pill: the office
// asked for a black label that stands out, and nothing else in the list is
// filled that dark.
const Label = styled.span<{ $variant: TFlagLabelVariant }>`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  background: ${RM_COLORS.gray900};
  color: ${RM_COLORS.white};
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: help;

  ${({ $variant }) => ($variant === "large" ? large : inline)}
`;

const getSinceText = (flag: IApplicationFlag) => {
  const date = formatDate(new Date(flag.createdAt), "d MMMM yyyy");

  return flag.createdByName
    ? getLocalizedText("application.flag.tooltip.since_by", {
        date,
        name: flag.createdByName,
      })
    : getLocalizedText("application.flag.tooltip.since", { date });
};

/**
 * The full record, for the remove modal: since when and by whom, at which
 * property, and why. The property is left out once it no longer exists.
 */
export const getFlagTooltip = (flag: IApplicationFlag) =>
  [
    getSinceText(flag),
    flag.propertyAddress
      ? getLocalizedText("application.flag.tooltip.property", {
          address: flag.propertyAddress,
        })
      : undefined,
    getLocalizedText("application.flag.tooltip.reason", {
      reason: flag.reason,
    }),
  ]
    .filter(Boolean)
    .join("\n");

/**
 * Shows the reason on hover, and on a tap for the tablet in visit mode, in the
 * same black as the label it explains.
 *
 * A styled Tooltip rather than a native `title`: the browser only shows a
 * title after the pointer rests still for about a second, never on a touch
 * screen, and nothing about the label says there is anything to wait for, so
 * the reason went unseen (Jonas, 2026-10-28).
 *
 * The wrapper stops the click, so tapping the label opens the tooltip without
 * also opening the row's drawer or folding the visit card.
 */
export const FlagTooltip = (props: {
  flag: IApplicationFlag;
  children: React.ReactNode;
}) => {
  const { flag, children } = props;

  return (
    <span
      style={{ display: "inline-flex", flexShrink: 0 }}
      onClick={event => event.stopPropagation()}
    >
      <Tooltip
        style={BARE_TOOLTIP_TRIGGER_STYLE}
        tooltipContentStyle={{
          backgroundColor: RM_COLORS.gray900,
          color: RM_COLORS.white,
          padding: "6px 10px",
          borderRadius: "6px",
          fontSize: "12px",
          lineHeight: "1.4",
        }}
        tooltipContent={
          <div style={{ maxWidth: "260px", whiteSpace: "pre-line" }}>
            <strong>{flag.reason}</strong>
            <div style={{ opacity: 0.75 }}>{getSinceText(flag)}</div>
          </div>
        }
      >
        {children}
      </Tooltip>
    </span>
  );
};

/**
 * The office's flag on a candidate. Read-only: it is set and removed
 * from the row menu in the candidate list, one candidate at a time.
 */
export const FlagLabel = (props: {
  flag: IApplicationFlag;
  /** "large" for the other-applications modal, "inline" next to a name. */
  variant?: TFlagLabelVariant;
}) => {
  const { flag, variant = "inline" } = props;

  return (
    <FlagTooltip flag={flag}>
      <Label $variant={variant}>
        {getLocalizedText("application.flag.label")}
      </Label>
    </FlagTooltip>
  );
};
